import { format } from 'date-fns';
import { ExecutionStats } from './ExecutionStats';
import { DatasetOutputGroups } from './DatasetOutputGroups';
import { RunningProgressView } from './RunningProgressView';
import { ExecutionLogPane } from './ExecutionLogPane';
import { ExecutionErrorsPanel } from '../ExecutionErrorsPanel';
import { MergeFailuresPanel } from '../MergeFailuresPanel';
import { ZeroRowInfoPanel } from './ZeroRowInfoPanel';
import { TableOverlapPanel } from './TableOverlapPanel';
import { SkippedResourcesPanel } from './SkippedResourcesPanel';
import { CloseOfBooksUnavailablePanel } from './CloseOfBooksUnavailablePanel';
import { StatusIcon } from '@/lib/executionStatus';
import type { ConnectorExecution, ExecutionErrorDetail } from '@/types/connectors';

interface Props { connectorType: string; execution: ConnectorExecution; }

const RUNNING_STATUSES = new Set(['pending', 'running']);

/** #2204: a user's name, an operator label, or "Scheduled". */
function describeRunBy(e: ConnectorExecution): string {
  if (e.triggered_by_name) return e.triggered_by_name;
  if (e.triggered_by_label) return e.triggered_by_label;
  if (e.triggered_by) return 'a user (name unavailable)';
  if (e.trigger_type === 'scheduled') return 'Scheduled';
  return e.trigger_type ? `${e.trigger_type} (unattributed)` : '';
}

export function ExecutionDetail({ connectorType, execution }: Props) {
  const start = execution.started_at ? format(new Date(execution.started_at), 'MMM d, yyyy h:mm a') : '—';
  const dur = execution.duration_seconds ? `${Math.round(execution.duration_seconds)}s` : '—';
  const isRunning = RUNNING_STATUSES.has(execution.status ?? '');
  const runBy = describeRunBy(execution);

  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      <header className="flex items-center gap-3 border-b p-4">
        <StatusIcon status={execution.status} size="h-5 w-5" />
        <div>
          <div className="text-sm font-semibold">{start}</div>
          <div className="text-xs text-muted-foreground">
            {isRunning ? 'Running' : `Completed in ${dur}`}{runBy ? ` · Run by ${runBy}` : ''}
          </div>
        </div>
      </header>
      <div className="space-y-5 p-4">
        {(execution.status === 'failed' || execution.status === 'partial') && (
          <ExecutionErrorsPanel
            errorDetails={
              Array.isArray((execution as any).error_details)
                ? ((execution as any).error_details as ExecutionErrorDetail[])
                : null
            }
            errorMessage={(execution as any).error_message ?? null}
          />
        )}
        <MergeFailuresPanel failures={(execution as any).merge_failures ?? null} />
        {/* #1880: shown on any terminal status -- a skip leaves a run green. */}
        <SkippedResourcesPanel resources={execution.skipped_resources} />
        {/* #2190: another enabled config writes the same tables. */}
        <TableOverlapPanel overlaps={execution.table_overlaps} />
        {/* #2228: the close-of-books check failed open on this run. */}
        <CloseOfBooksUnavailablePanel cob={execution.cob_unavailable} />
        {execution.status === 'success' &&
          ((execution as any).zero_row_resources?.length ?? 0) > 0 && (
            <ZeroRowInfoPanel resources={(execution as any).zero_row_resources} />
          )}
        <ExecutionStats connectorType={connectorType} execution={execution} />
        {isRunning ? (
          <RunningProgressView execution={execution} />
        ) : (
          <>
            {(execution.log?.length ?? 0) > 0 && (
              <ExecutionLogPane log={execution.log} title="Run log" />
            )}
            <DatasetOutputGroups connectorType={connectorType} executionId={execution.execution_id} />
          </>
        )}
      </div>
    </div>
  );
}
